"use client"

import React, { useEffect, useState, useContext, useMemo } from "react"
import { Link, useParams } from "react-router-dom"
import axios from "axios"
import { motion } from "framer-motion"
import { Calendar, Clock, ChevronRight, ExternalLink, MapPin } from "lucide-react"
import { CircuitoContext } from "../../context/CircuitoContext"
import "../../styles/circuitoHomeV2.css"

const API = process.env.REACT_APP_API_URL || "https://api.ticketdovaqueiro.com/api"

export default function CircuitoEtapas() {
  const { slug } = useParams()
  const { circuito, eventosCircuito, loadingCircuito } = useContext(CircuitoContext)
  
  const [etapasManuais, setEtapasManuais] = useState([])
  const [temposRestantes, setTemposRestantes] = useState({})

  // 💡 DETECTOR DE DOMÍNIO EXCLUSIVO
  const isCustomDomain = window.location.hostname !== "ticketdovaqueiro.com" && window.location.hostname !== "www.ticketdovaqueiro.com" && window.location.hostname !== "localhost";

  // Busca as etapas manuais
  useEffect(() => {
    if (!circuito?._id) return
    axios
      .get(`${API}/circuitos/${circuito._id}/etapas-manuais`)
      .then((res) => setEtapasManuais(res.data || []))
      .catch(() => setEtapasManuais([]))
  }, [circuito?._id])

  // Unifica e calcula o status real das etapas
  const etapasUnificadas = useMemo(() => {
    const internas = (eventosCircuito || []).map((e, index) => ({
      id: e._id,
      nome: e.nome,
      imagem: e.imagem,
      dataInicio: e.dataInicio,
      dataFim: e.dataFim,
      tipo: "interna",
      // 💡 SE FOR DOMÍNIO EXTERNO, FORÇA A URL DA TICKET
      link: isCustomDomain ? `https://ticketdovaqueiro.com/evento/${e._id}` : `/evento/${e._id}`,
      statusReal: e.status, // "aberto", "em breve", "encerrado", "ocultado"
      ordem: index + 1,
    }))

    const manuais = (etapasManuais || []).map((e, index) => {
      const start = new Date(e.dataInicioVendas)
      const end = new Date(e.dataFimVendas)
      const now = new Date()
      
      let status = "aberto"
      if (now < start) status = "em breve"
      else if (now > end) status = "encerrado"

      return {
        id: e._id,
        nome: e.nome,
        imagem: e.imagemCartaz,
        dataInicio: e.dataInicioVendas,
        dataFim: e.dataFimVendas,
        tipo: "externa",
        link: e.linkExterno,
        statusReal: status,
        ordem: 999 + index,
      }
    })

    return [...internas, ...manuais]
      .filter((e) => e.statusReal !== "ocultado")
      .sort((a, b) => new Date(a.dataInicio) - new Date(b.dataInicio))
  }, [eventosCircuito, etapasManuais, isCustomDomain])

  // Relógio Regressivo
  const calcularTempoRestante = (evento) => {
    let targetDate
    if (evento.statusReal === "em breve") {
      targetDate = new Date(evento.dataInicio).getTime()
    } else if (evento.statusReal === "aberto") {
      targetDate = new Date(evento.dataFim).getTime()
    } else {
      return { dias: 0, horas: 0, minutos: 0, segundos: 0, zerado: true }
    }

    const agora = new Date().getTime()
    const diferenca = targetDate - agora

    if (diferenca <= 0) {
      return { dias: 0, horas: 0, minutos: 0, segundos: 0, zerado: true }
    }

    return {
      dias: Math.floor(diferenca / (1000 * 60 * 60 * 24)),
      horas: Math.floor((diferenca % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutos: Math.floor((diferenca % (1000 * 60 * 60)) / (1000 * 60)),
      segundos: Math.floor((diferenca % (1000 * 60)) / 1000),
      zerado: false,
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      const novosTempos = {}
      etapasUnificadas.forEach((evento) => {
        novosTempos[evento.id] = calcularTempoRestante(evento)
      })
      setTemposRestantes(novosTempos)
    }, 1000)
    return () => clearInterval(interval)
  }, [etapasUnificadas])

  const formatarData = (dataString) => {
    if (!dataString) return "-"
    return new Date(dataString).toLocaleDateString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit"
    }).replace(",", " às")
  }

  if (loadingCircuito) {
    return (
      <div className="circuito-shell">
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="circuito-shell">
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "40px 24px" }}>
        
        {/* Cabeçalho da Página */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <h1 style={{ fontSize: "36px", fontWeight: 800, color: "#fff", marginBottom: 8 }}>
            Etapas do <span style={{ color: "var(--c-accent)" }}>Circuito</span>
          </h1>
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "16px", maxWidth: 600, margin: "0 auto" }}>
            Acompanhe o calendário oficial, veja os prazos de virada de lote e garanta sua senha nas próximas vaquejadas.
          </p>
        </div>

        {etapasUnificadas.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", background: "rgba(255,255,255,0.02)", borderRadius: 16, border: "1px dashed rgba(255,255,255,0.1)" }}>
            <Calendar size={48} color="var(--c-accent)" style={{ margin: "0 auto 16px", opacity: 0.5 }} />
            <h3 style={{ color: "#fff", fontSize: "18px", marginBottom: 8 }}>Nenhuma etapa cadastrada</h3>
            <p style={{ color: "rgba(255,255,255,0.5)" }}>Em breve o calendário oficial será divulgado.</p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 24 }}>
            {etapasUnificadas.map((etapa, idx) => {
              const timer = temposRestantes[etapa.id]
              const isEncerrado = etapa.statusReal === "encerrado" || (timer && timer.zerado && etapa.statusReal !== "em breve")
              const isEmBreve = etapa.statusReal === "em breve"

              return (
                <motion.div 
                  key={etapa.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  style={{
                    background: "var(--c-card)",
                    borderRadius: "16px",
                    border: "1px solid rgba(255,255,255,0.06)",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.2)"
                  }}
                >
                  {/* Imagem e Badge */}
                  <div style={{ position: "relative", height: 200, width: "100%", background: "#000" }}>
                    <img 
                      src={`https://api.ticketdovaqueiro.com/uploads/${etapa.imagem}`} 
                      alt={etapa.nome}
                      style={{ width: "100%", height: "100%", objectFit: "cover", opacity: isEncerrado ? 0.4 : 0.9 }}
                    />
                    <div style={{
                      position: "absolute", top: 12, left: 12,
                      padding: "4px 12px", borderRadius: "8px", fontSize: "11px", fontWeight: 800, textTransform: "uppercase",
                      background: isEncerrado ? "rgba(0,0,0,0.7)" : isEmBreve ? "#f59e0b" : "var(--c-accent)",
                      color: isEncerrado ? "#fff" : "#000",
                      backdropFilter: "blur(4px)"
                    }}>
                      {isEncerrado ? "Vendas Encerradas" : isEmBreve ? "Em Breve" : "Vendas Abertas"}
                    </div>
                  </div>

                  {/* Corpo do Card */}
                  <div style={{ padding: "20px", display: "flex", flexDirection: "column", flex: 1 }}>
                    <h3 style={{ fontSize: "18px", fontWeight: 700, color: "#fff", marginBottom: 12, lineHeight: 1.3 }}>
                      {etapa.nome}
                    </h3>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20, fontSize: "13px", color: "rgba(255,255,255,0.6)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Calendar size={14} color="var(--c-accent)" />
                        <span>Início: {formatarData(etapa.dataInicio)}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Clock size={14} color="var(--c-accent)" />
                        <span>Fim: {formatarData(etapa.dataFim)}</span>
                      </div>
                    </div>

                    {/* Contador Regressivo Bonitão */}
                    {!isEncerrado && timer && (
                      <div style={{ marginBottom: 24, marginTop: "auto" }}>
                        <div style={{ fontSize: "11px", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>
                          {isEmBreve ? "Abertura das vendas em:" : "Vendas encerram em:"}
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          {[
                            { v: timer.dias, l: "Dias" },
                            { v: timer.horas, l: "Hrs" },
                            { v: timer.minutos, l: "Min" },
                            { v: timer.segundos, l: "Seg" }
                          ].map((t, i) => (
                            <div key={i} style={{ 
                              flex: 1, background: "rgba(255,255,255,0.03)", 
                              border: "1px solid rgba(255,255,255,0.05)", 
                              borderRadius: 8, padding: "8px 4px", textAlign: "center" 
                            }}>
                              <strong style={{ display: "block", color: "var(--c-accent)", fontSize: "18px", lineHeight: 1 }}>
                                {String(t.v).padStart(2, "0")}
                              </strong>
                              <span style={{ fontSize: "9px", opacity: 0.5, textTransform: "uppercase" }}>{t.l}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Botões de Ação */}
                    {isEncerrado ? (
                      <div style={{ width: "100%", padding: "12px", textAlign: "center", background: "rgba(255,255,255,0.05)", borderRadius: 12, color: "rgba(255,255,255,0.4)", fontSize: "14px", fontWeight: 600, marginTop: "auto" }}>
                        Evento Encerrado
                      </div>
                    ) : etapa.tipo === "interna" ? (
                      
                      /* 💡 CONDIÇÃO ATUALIZADA AQUI: Se for domínio externo, usa <a> para sair para a Ticket */
                      isCustomDomain ? (
                        <a href={etapa.link} style={{ textDecoration: "none", marginTop: "auto" }}>
                          <button className={isEmBreve ? "c-btn c-btn--outline" : "c-btn c-btn--primary"} style={{ width: "100%", height: 48, fontSize: 14, fontWeight: 700, display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
                            {isEmBreve ? "Ver Detalhes" : "Comprar Senha"} <ChevronRight size={16} />
                          </button>
                        </a>
                      ) : (
                        <Link to={etapa.link} style={{ textDecoration: "none", marginTop: "auto" }}>
                          <button className={isEmBreve ? "c-btn c-btn--outline" : "c-btn c-btn--primary"} style={{ width: "100%", height: 48, fontSize: 14, fontWeight: 700, display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
                            {isEmBreve ? "Ver Detalhes" : "Comprar Senha"} <ChevronRight size={16} />
                          </button>
                        </Link>
                      )

                    ) : (
                      <a href={etapa.link} target="_blank" rel="noreferrer" style={{ textDecoration: "none", marginTop: "auto" }}>
                        <button className="c-btn c-btn--primary" style={{ width: "100%", height: 48, fontSize: 14, fontWeight: 700, display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
                          Acessar Evento <ExternalLink size={16} />
                        </button>
                      </a>
                    )}

                  </div>
                </motion.div>
              )
            })}
          </div>
        )}

      </div>
    </div>
  )
}